import React from 'react';
import { useTranslation } from 'react-i18next';
import { RenderedHtml } from 'modules/editor';
import { getQuestionOptionLabel } from 'lib/questionOptions';
import { cn } from 'lib/utils';
import { Checkbox } from 'components/ui/checkbox';
import { Input } from 'components/ui/input';
import { MatchingPlayer } from 'components/questions/MatchingPlayer';
import { FillInBlanksPlayer } from 'components/questions/FillInBlanksPlayer';
import { ReorderPlayer } from 'components/questions/ReorderPlayer';
import { OpenEndedPlayer } from 'components/questions/OpenEndedPlayer';
import { SprInput } from './SprInput';
import type { CandidateQuestion } from '../../types';
import { SELECTED_MARK, SELECTED_OPTION } from './selection';

interface Props {
  question: CandidateQuestion;
  value: unknown;
  onChange: (value: unknown) => void;
  /** SAT answer eliminator: strike options out without choosing them. */
  eliminatorOn?: boolean;
  eliminated?: Set<string>;
  onToggleEliminated?: (optionId: string) => void;
  /** Option letters A, B, C… like the real papers. */
  lettered?: boolean;
}

const LETTERS = 'ABCDEFGHIJ';

/** Answer controls for one question, by type. Prompts are rendered by the caller. */
export function ExamAnswerArea({
  question,
  value,
  onChange,
  eliminatorOn,
  eliminated,
  onToggleEliminated,
  lettered = true,
}: Props) {
  const { t } = useTranslation();
  const q = question;
  const options = (q.options ?? []).map((o, i) => (typeof o === 'string' ? { id: String(i), value: o } : o));

  if (q.type === 'MULTIPLE_CHOICE' || q.type === 'TRUE_FALSE' || q.type === 'MULTIPLE_RESPONSE') {
    const multi = q.type === 'MULTIPLE_RESPONSE';
    const selected = multi ? (Array.isArray(value) ? (value as string[]) : []) : typeof value === 'string' ? [value] : [];
    return (
      <div role={multi ? 'group' : 'radiogroup'} aria-label={t('exam.answer_choices')} className="flex flex-col gap-2.5">
        {options.map((option, index) => {
          const isSelected = selected.includes(option.id);
          const isOut = eliminated?.has(option.id) ?? false;
          const choose = () => {
            if (isOut) onToggleEliminated?.(option.id); // choosing a struck option brings it back
            if (multi) onChange(isSelected ? selected.filter((id) => id !== option.id) : [...selected, option.id]);
            else onChange(option.id);
          };
          return (
            <div key={option.id} className="flex items-center gap-2">
              <button
                type="button"
                role={multi ? 'checkbox' : 'radio'}
                aria-checked={isSelected}
                onClick={choose}
                className={cn(
                  'group flex min-h-12 flex-1 items-center gap-3 rounded-lg border-2 px-3 py-2.5 text-left transition-colors',
                  isSelected ? SELECTED_OPTION : 'border-gray-300 bg-white hover:border-gray-500',
                  isOut && 'opacity-50',
                )}
              >
                {multi ? (
                  <Checkbox checked={isSelected} tabIndex={-1} aria-hidden className="pointer-events-none" />
                ) : (
                  lettered && (
                    <span
                      className={cn(
                        'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 text-sm font-semibold',
                        isSelected ? SELECTED_MARK : 'border-gray-700 text-gray-800',
                      )}
                    >
                      {LETTERS[index] ?? index + 1}
                    </span>
                  )
                )}
                <span className={cn('text-[15px] leading-snug text-gray-900', isOut && 'line-through decoration-2')}>
                  <RenderedHtml html={getQuestionOptionLabel(q.type, option.value || '', t)} inline />
                </span>
              </button>
              {eliminatorOn && onToggleEliminated && (
                <button
                  type="button"
                  onClick={() => onToggleEliminated(option.id)}
                  aria-pressed={isOut}
                  aria-label={isOut ? t('exam.undo_eliminate', { letter: LETTERS[index] }) : t('exam.eliminate', { letter: LETTERS[index] })}
                  className={cn(
                    'flex h-9 shrink-0 items-center justify-center text-xs font-semibold',
                    isOut
                      ? 'w-14 text-slate-700 underline underline-offset-2 hover:text-slate-900'
                      : 'w-9 rounded-full border border-slate-300 bg-white text-slate-500 line-through hover:bg-slate-50',
                  )}
                >
                  {isOut ? t('exam.undo') : LETTERS[index] ?? index + 1}
                </button>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  if (q.type === 'SPR') {
    return <SprInput value={typeof value === 'string' ? value : ''} onChange={onChange} />;
  }

  if (q.type === 'SHORT_ANSWER') {
    return (
      <div className="max-w-md space-y-1.5">
        {q.maxWords ? <p className="text-sm text-gray-600">{t('reading_word_limit', { count: q.maxWords })}</p> : null}
        <Input
          aria-label={t('answer')}
          placeholder={t('type_your_answer_here')}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
          className="min-h-11 text-[15px]"
        />
      </div>
    );
  }

  if (q.type === 'MATCHING') {
    return (
      <MatchingPlayer
        pairs={q.matchPairs ?? []}
        options={q.matchingOptions}
        value={(value as Record<string, string>) || {}}
        onChange={onChange}
      />
    );
  }

  if (q.type === 'FILL_IN_BLANKS' && q.blanks) {
    return (
      <FillInBlanksPlayer
        prompt={q.prompt || ''}
        blanks={q.blanks}
        value={(value as Record<string, string>) || {}}
        onChange={onChange}
      />
    );
  }

  if (q.type === 'REORDER' && q.reorderItems) {
    return <ReorderPlayer items={q.reorderItems} value={(value as string[]) || []} onChange={onChange} />;
  }

  if (q.type === 'OPEN_ENDED') {
    return <OpenEndedPlayer value={typeof value === 'string' ? value : ''} onChange={onChange} maxWords={q.maxWords} />;
  }

  return null;
}

/** Types whose control already shows the prompt (the caller must not repeat it). */
export const PROMPT_OWNED_TYPES = new Set(['FILL_IN_BLANKS', 'PASSAGE']);

export default ExamAnswerArea;
